import React from 'react';
import { useSelector } from 'react-redux';
import { HKPlatform } from 'core/constants';
import type { RootState } from 'core/store';
import './InventoryDetailSkeletonLoader.scss';

interface InventoryDetailSkeletonLoaderProps {
  // Home Selections has no image on either platform (just the "Option
  // Details" card/list) — set false there so the card/section goes full
  // width instead of leaving a phantom image placeholder next to/above it.
  showImage?: boolean;
}

/**
 * Skeletal loader for any item detail page (product, home selection, color
 * selection — mobile modal + desktop route). Shown while `productDetailsLoading`
 * / `loading` is true. The desktop shape mirrors
 * `.hk-desktop-digital-inventory-details-container`'s real layout (480px
 * square image + a bordered card with a header row and label/value attribute
 * rows) so there's no visual jump when real content mounts; the mobile shape
 * mirrors `InventoryDetailsMobile.tsx`'s stacked, card-less layout.
 */
const InventoryDetailSkeletonLoader: React.FC<
  InventoryDetailSkeletonLoaderProps
> = ({ showImage = true }) => {
  const { platformType, isDesktopWidth } = useSelector(
    (state: RootState) => state.platform
  );
  const isDesktop = platformType === HKPlatform.DESKTOP && !!isDesktopWidth;

  const attributeRows = [0, 1, 2, 3, 4].map((i) => (
    <div
      key={`skeleton-attr-${i}`}
      className="hk-inventory-detail-skeleton-attribute"
    >
      <div className="hk-skeleton-oval hk-inventory-detail-skeleton-attribute-label" />
      <div className="hk-skeleton-rectangle hk-inventory-detail-skeleton-attribute-value" />
    </div>
  ));

  if (isDesktop) {
    return (
      <div className="hk-inventory-detail-skeleton-desktop">
        {showImage && (
          <div className="hk-skeleton-rectangle hk-inventory-detail-skeleton-image" />
        )}
        <div className="hk-inventory-detail-skeleton-card">
          <div className="hk-inventory-detail-skeleton-card-header">
            <div className="hk-skeleton-oval hk-inventory-detail-skeleton-header" />
          </div>
          <div className="hk-inventory-detail-skeleton-card-content">
            {attributeRows}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hk-inventory-detail-skeleton">
      {showImage && (
        <div className="hk-skeleton-rectangle hk-inventory-detail-skeleton-image" />
      )}
      <div className="hk-inventory-detail-skeleton-section">
        <div className="hk-skeleton-oval hk-inventory-detail-skeleton-header" />
        {attributeRows}
      </div>
    </div>
  );
};

export default InventoryDetailSkeletonLoader;
